/* eslint-disable no-unused-vars */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';


const ScrollInterceptSection = () => {
    const [isHoveringCards, setIsHoveringCards] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [selectedCategory] = useState('all');
    const [scrollDistance, setScrollDistance] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [hasInitialRotationPlayed, setHasInitialRotationPlayed] = useState(false);

    const rotationAngle = useRef(0);
    const sectionRef = useRef(null);
    const carouselRef = useRef(null);
    const carouselWrapperRef = useRef(null);
    const totalScrollNeeded = 3500;
    const initialRotationAnimation = useRef(null);

    // Touch event variables
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const isTouching = useRef(false);
    const touchThreshold = 50; // Minimum swipe distance

    // Updated services data - Digital Marketing + Web Design as one service
    const cards = [
        // Digital Marketing Services
        {
            id: 1,
            category: 'seo',
            title: 'SEO',
            description: "SEO That's Built for the Future of Search",
            features: ['AI SEO', 'GEO (Generative Engine Optimisation)', 'Technical SEO', 'Ecommerce SEO'],
            ctaText: 'Start SEO Strategy'
        },
        {
            id: 2,
            category: 'paid-media',
            title: 'Paid Media',
            description: 'High-Performance Paid Advertising',
            features: ['PPC (Google & Search-Led Campaigns)', 'Paid Social', 'Programmatic Advertising'],
            ctaText: 'Launch Paid Campaign'
        },
        {
            id: 3,
            category: 'data',
            title: 'Data',
            description: 'Marketing Powered by Data',
            features: ['Data Science', 'Analytics', 'Tag Management'],
            ctaText: 'Optimize With Data'
        },
        {
            id: 4,
            category: 'strategy',
            title: 'Strategy',
            description: 'Clarity Before Scale',
            features: ['Audits & Consultancy', 'Market Research', 'Media Planning', 'International Marketing Strategy'],
            ctaText: 'Get Strategic Plan'
        },
        {
            id: 5,
            category: 'experience',
            title: 'Experience',
            description: 'Digital Experiences That Convert',
            features: ['Creative & Performance Design', 'CRO', 'Technology', 'Video'],
            ctaText: 'Enhance Experience'
        },
        // Web Design & UI-UX as a single service with all sub-features
        {
            id: 6,
            category: 'web-design',
            title: 'Web Design & UI-UX',
            description: 'Complete digital solutions',
            features: [
                'UI/UX Design',
                'Frontend Development',
                'Backend Development',
            ],
            ctaText: 'Build Website'
        }
    ];

    const filteredCards = selectedCategory === 'all'
        ? cards
        : cards.filter(card => card.category === selectedCategory);

    const calculateCardPosition = (index, total) => {
        const angle = (360 / total) * index;
        const radius = isMobile ? 250 : 400; // Smaller radius on mobile
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const z = Math.sin((angle * Math.PI) / 180) * radius;
        const scale = 3 - Math.abs(z) / 1000;

        return {
            transform: `translate3d(${x}px, 0, ${z}px) rotateY(${90 - angle}deg)`,
            '--card-scale': scale,
            zIndex: Math.round(1000 - Math.abs(z)),
        };
    };

    // Function to perform initial rotation animation
    const performInitialRotation = useCallback(() => {
        if (hasInitialRotationPlayed || !carouselRef.current) return;

        // Cancel any existing animation
        if (initialRotationAnimation.current) {
            cancelAnimationFrame(initialRotationAnimation.current);
        }

        const startTime = performance.now();
        const startRotation = rotationAngle.current;
        const targetRotation = 360; // Full rotation
        const duration = 1000; // 1 second

        const animatedRotation = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function for smooth animation
            const easeOut = 1 - Math.pow(1 - progress, 3);

            rotationAngle.current = startRotation + (targetRotation * easeOut);

            if (carouselRef.current) {
                carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(${rotationAngle.current}deg)`;
            }

            if (progress < 1) {
                initialRotationAnimation.current = requestAnimationFrame(animatedRotation);
            } else {
                // Reset to 0 after completion
                rotationAngle.current = 0;
                if (carouselRef.current) {
                    carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(0deg)`;
                }
                setHasInitialRotationPlayed(true);
                initialRotationAnimation.current = null;
            }
        };

        initialRotationAnimation.current = requestAnimationFrame(animatedRotation);
    }, [hasInitialRotationPlayed]);

    // Check if device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => {
            window.removeEventListener('resize', checkIfMobile);
        };
    }, []);

    const handleCarouselMouseEnter = useCallback(() => {
        if (!sectionRef.current || isHoveringCards || isMobile) return;

        const section = sectionRef.current;
        const rect = section.getBoundingClientRect();

        section.style.width = `${rect.width}px`;
        section.style.height = `${rect.height}px`;
        section.style.zIndex = '1000';

        setIsHoveringCards(true);
        setScrollDistance(0);
    }, [isHoveringCards, isMobile]);

    const handleScroll = useCallback((e) => {
        if (!isHoveringCards || !sectionRef.current || isMobile) return;

        e.preventDefault();
        e.stopPropagation();

        const scrollDelta = e.deltaY;
        const rotationSpeed = 0.15;

        rotationAngle.current += scrollDelta * rotationSpeed;

        setScrollDistance(prev => {
            const newDistance = prev + Math.abs(scrollDelta);

            if (newDistance >= totalScrollNeeded) {
                restoreNormalScroll();
                return 0;
            }

            return newDistance;
        });

        if (carouselRef.current) {
            carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(${rotationAngle.current}deg)`;
        }
    }, [isHoveringCards, totalScrollNeeded, isMobile]);

    // Touch event handlers for mobile
    const handleTouchStart = useCallback((e) => {
        if (!isMobile) return;

        touchStartX.current = e.touches[0].clientX;
        isTouching.current = true;
    }, [isMobile]);

    const handleTouchMove = useCallback((e) => {
        if (!isMobile || !isTouching.current) return;

        e.preventDefault();
        touchEndX.current = e.touches[0].clientX;

        // Calculate swipe distance
        const swipeDistance = touchEndX.current - touchStartX.current;
        const rotationSpeed = 0.5; // Adjust sensitivity for touch

        // Apply rotation in real-time during swipe
        rotationAngle.current += swipeDistance * rotationSpeed;

        if (carouselRef.current) {
            carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(${rotationAngle.current}deg)`;
        }

        // Update start position for continuous tracking
        touchStartX.current = touchEndX.current;
    }, [isMobile]);

    const handleTouchEnd = useCallback((e) => {
        if (!isMobile) return;

        isTouching.current = false;

        // Calculate final swipe distance
        const finalSwipeDistance = touchEndX.current - touchStartX.current;

        // Only add to scroll distance for significant swipes
        if (Math.abs(finalSwipeDistance) > touchThreshold) {
            setScrollDistance(prev => {
                const newDistance = prev + Math.abs(finalSwipeDistance);

                if (newDistance >= totalScrollNeeded) {
                    // Reset after completing the scroll
                    setTimeout(() => {
                        rotationAngle.current = 0;
                        if (carouselRef.current) {
                            carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(0deg)`;
                        }
                        setScrollDistance(0);
                    }, 300);
                    return 0;
                }

                return newDistance;
            });
        }
    }, [isMobile, totalScrollNeeded]);

    const restoreNormalScroll = useCallback(() => {
        if (!sectionRef.current || !isHoveringCards) return;

        const section = sectionRef.current;

        section.style.position = '';
        section.style.top = '';
        section.style.width = '';
        section.style.height = '';
        section.style.zIndex = '';

        document.body.style.paddingTop = '';

        setIsHoveringCards(false);
        rotationAngle.current = 0;
        setScrollDistance(0);

        if (carouselRef.current) {
            carouselRef.current.style.transform = `translate(-50%, -50%) rotateY(0deg)`;
        }
    }, [isHoveringCards]);

    useEffect(() => {
        const section = sectionRef.current;
        const carouselWrapper = carouselWrapperRef.current;

        if (!section || !carouselWrapper) return;

        carouselWrapper.addEventListener('mouseenter', handleCarouselMouseEnter);

        // Add touch event listeners for mobile
        carouselWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
        carouselWrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
        carouselWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

        if (isHoveringCards && !isMobile) {
            carouselWrapper.addEventListener('wheel', handleScroll, { passive: false });
        } else {
            carouselWrapper.removeEventListener('wheel', handleScroll);
        }

        const handleScrollActivation = () => {
            const rect = section.getBoundingClientRect();
            const isInViewport = rect.top <= window.innerHeight * 0.5 && rect.bottom >= window.innerHeight * 0.5;
            setIsActive(isInViewport);

            // Trigger initial rotation when section becomes active and rotation hasn't played yet
            if (isInViewport && !hasInitialRotationPlayed && !isMobile) {
                performInitialRotation();
            }
        };

        window.addEventListener('scroll', handleScrollActivation);
        handleScrollActivation();

        return () => {
            carouselWrapper.removeEventListener('mouseenter', handleCarouselMouseEnter);
            carouselWrapper.removeEventListener('wheel', handleScroll);
            carouselWrapper.removeEventListener('touchstart', handleTouchStart);
            carouselWrapper.removeEventListener('touchmove', handleTouchMove);
            carouselWrapper.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('scroll', handleScrollActivation);

            // Clean up animation frame
            if (initialRotationAnimation.current) {
                cancelAnimationFrame(initialRotationAnimation.current);
            }
        };
    }, [isHoveringCards, isMobile, handleCarouselMouseEnter, handleScroll, handleTouchStart, handleTouchMove, handleTouchEnd, hasInitialRotationPlayed, performInitialRotation]);

    const handleCardClick = (cardId) => {
        console.log(`Service ${cardId} clicked`);
    };

    return (
        <div
            ref={sectionRef}
            className={`scroll-intercept-section ${isActive ? 'active' : ''}`}
        >
            <div className="section-container">
                <div className="section-header w-100">
                    <h1 className="services-section-title">
                        <span className="services-title-gradient">Our Core Services</span>
                    </h1>
                    <h3 className="section-subtitle">
                        Scalable digital marketing services designed for measurable growth.
                    </h3>
                </div>

                <div ref={carouselWrapperRef} className="carousel-wrapper" >
                    <div
                        ref={carouselRef}
                        className="interactive-carousel"
                    >
                        {filteredCards.map((card, index) => {
                            const position = calculateCardPosition(index, filteredCards.length);
                            return (
                                <div
                                    key={card.id}
                                    className="service-card-wrapper"
                                    style={position}
                                >
                                    <div
                                        className="service-card"
                                        onClick={() => handleCardClick(card.id)}
                                    >
                                        <div className="card-inner">
                                            <div className="card-header">
                                                <div className="card-category">{card.category.toUpperCase()}</div>
                                            </div>

                                            <h3 className="card-title">{card.title}</h3>
                                            <p className="card-description">{card.description}</p>

                                            <div className="card-features">
                                                {card.features.map((feature, idx) => (
                                                    <span key={idx} className="feature">
                                                        • {feature}
                                                    </span>
                                                ))}
                                            </div>

                                            <Link
                                                to="/contact-form"
                                                className="card-action"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {card.ctaText}
                                                <span className="action-arrow">→</span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScrollInterceptSection;